import { useState } from "react";
// Third party
import clsx from "clsx";
import { useMutation } from "@tanstack/react-query";
import Joi from "joi";
import { motion } from "framer-motion";

// MUI
import { Button, Divider, Link, Stack, SvgIcon, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Components
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";

// Api
import { requestClientAccessSendEmail } from "@/Settings/api/AdministrationTools/mutations";

// Types
import { IRequestClientAccessResponse } from "@/Settings/types/administrationTools/utils";

// Icons
import { ReactComponent as EmailIcon } from "../../../assets/email-outlined.svg";
import { or, and } from "@/Common/helpers/sonar";

interface IAuthenticationLinkProps {
	locationId: string | undefined;
	requestLink: IRequestClientAccessResponse;
}

// Joi validation schema
const emailValidationSchema = Joi.object({
	emailAddress: Joi.string()
		.email({ tlds: { allow: false } })
		.messages({
			"string.email": "Please enter a valid email address",
		}),
});

const AuthenticationLink = (props: IAuthenticationLinkProps) => {
	const { locationId, requestLink } = props;
	const [emailAddress, setEmailAddress] = useState<string>("");
	const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
	const [emailError, setEmailError] = useState<string>("");

	/***************************************** QUERIES *****************************************/
	const { mutate, isPending } = useMutation({
		mutationKey: ["payroll-provider-request-client-access--send-email", emailAddress, requestLink.providerName],
		mutationFn: async () => {
			if (!locationId) {
				return null;
			}
			return requestClientAccessSendEmail(locationId, requestLink.providerName, emailAddress, requestLink.approvalLink);
		},
		onSettled: () => {
			setShowEmailForm(false);
			setEmailAddress("");
			setEmailError("");
		},
	});

	/***************************************** HANDLERS ****************************************/

	const validateEmail = (email: string) => {
		if (!email.trim()) {
			setEmailError("");
			return true;
		}

		const { error } = emailValidationSchema.validate({ emailAddress: email });
		if (error) {
			setEmailError(error.details[0].message);
			return false;
		}
		setEmailError("");
		return true;
	};

	const handleChangeEmailAddress = (value: string) => {
		setEmailAddress(value);
		validateEmail(value);
	};

	const handleShareViaEmail = () => {
		if (showEmailForm) {
			return;
		}
		setShowEmailForm(!showEmailForm);
		if (!showEmailForm) {
			setEmailAddress("");
			setEmailError("");
		}
	};

	const handleSendEmail = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!validateEmail(emailAddress)) {
			return;
		}

		if (or(!emailAddress.trim(), !locationId)) {
			return;
		}
		mutate();
	};

	return (
		<Stack direction="column" spacing={1} mt={2}>
			<Typography className="!font-sans !font-normal !text-sm !text-default mb-1">
				{requestLink.providerName} Authentication Link
			</Typography>
			<Stack direction={"row"} spacing={1} alignItems={"center"}>
				<Link
					href={requestLink.approvalLink}
					className="!text-sm !font-sans !font-normal !text-primary !whitespace-nowrap !text-ellipsis !overflow-hidden !border !border-disabled !rounded-md !p-1"
					target="_blank"
					rel="noopener noreferrer"
					underline="none"
				>
					{requestLink.approvalLink}
				</Link>
				<Button
					variant="contained"
					disableElevation
					onClick={() => navigator.clipboard.writeText(requestLink.approvalLink)}
					classes={{
						root: clsx(
							"!h-[30px] !w-[56px] !rounded-md !font-sans !text-sm !text-white !font-normal !normal-case",
							"!bg-primary"
						),
					}}
				>
					Copy
				</Button>
			</Stack>
			<Divider className="!my-6" />
			<Stack
				direction={"row"}
				spacing={1}
				alignItems={"center"}
				className={clsx("!mt-0 ", { "!cursor-pointer": !showEmailForm })}
				onClick={handleShareViaEmail}
			>
				<SvgIcon
					component={EmailIcon}
					className={clsx("!w-5 !h-4", {
						"!fill-primary": !showEmailForm,
						"!fill-disabled": showEmailForm,
					})}
					viewBox="0 0 20 17"
				/>
				<Typography className="!font-sans !font-normal !text-sm !text-default">Share link via email</Typography>
			</Stack>
			<motion.div
				initial={false}
				animate={{
					height: showEmailForm ? "auto" : 0,
					opacity: showEmailForm ? 1 : 0,
				}}
				transition={{
					duration: 0.3,
					ease: "easeInOut",
				}}
				style={{ overflow: "hidden" }}
			>
				{showEmailForm && (
					<motion.form
						className="!mt-2"
						onSubmit={handleSendEmail}
						initial={{ y: -10, opacity: 0 }}
						animate={{ y: 0, opacity: 1 }}
						exit={{ y: -10, opacity: 0 }}
						transition={{ duration: 0.2, delay: 0.1 }}
					>
						<Stack direction="row" spacing={1}>
							<Stack direction="column" spacing={1} flex={1}>
								<SimpleTextField
									editable
									outlined
									type={"email"}
									text={emailAddress}
									placeholder="recipient@domain.com"
									error={!!emailError}
									onChange={handleChangeEmailAddress}
								/>
								{emailError && (
									<Typography className="!font-sans !font-normal !text-xs !text-alert">{emailError}</Typography>
								)}
							</Stack>
							<LoadingButton
								variant="contained"
								type="submit"
								disableElevation
								onSubmit={() => handleSendEmail}
								disabled={or(!emailAddress.trim(), isPending, !!emailError)}
								loading={isPending}
								fullWidth
								classes={{
									root: clsx(
										"!h-[30px] !w-[56px] !rounded-md !font-sans !text-sm !text-white !font-normal !normal-case",
										{
											"!bg-disabled !fill-white": or(!emailAddress.trim(), isPending, !!emailError),
											"!bg-primary": and(!!emailAddress.trim(), !isPending, !emailError),
										}
									),
									loadingIndicator: "!text-white",
								}}
							>
								{isPending ? "" : "Send"}
							</LoadingButton>
						</Stack>
					</motion.form>
				)}
			</motion.div>
		</Stack>
	);
};

export default AuthenticationLink;
